import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { 
  Plus, Search, Users, Globe, Lock, X, ChevronRight, Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  "Technologie", "Business", "Design", "Marketing", "Finance", 
  "Santé", "Éducation", "Art & Créativité", "Développement Personnel", "Autre"
];

const CommunityList = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [communities, setCommunities] = useState([]);
  const [myCommunities, setMyCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [newCommunity, setNewCommunity] = useState({
    name: '',
    description: '',
    category: 'Technologie',
    is_public: true,
    join_mode: 'open',
    welcome_message: ''
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadCommunities();
    if (currentUser) {
      loadMyCommunities();
    }
  }, [currentUser]);

  const loadCommunities = async () => {
    try {
      const response = await axios.get(`${API}/communities`, {
        withCredentials: true
      });
      setCommunities(response.data.communities);
    } catch (error) {
      console.error('Error loading communities:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMyCommunities = async () => {
    try {
      const response = await axios.get(`${API}/users/${currentUser.user_id}/communities`, {
        withCredentials: true
      });
      setMyCommunities(response.data.communities);
    } catch (error) {
      console.error('Error loading my communities:', error);
    }
  };

  const handleCreateCommunity = async () => {
    if (!newCommunity.name) return;
    
    setCreating(true);
    try {
      const response = await axios.post(`${API}/communities`, newCommunity, {
        withCredentials: true
      });
      setShowCreateModal(false);
      setNewCommunity({ name: '', description: '', category: 'Technologie', is_public: true, join_mode: 'open', welcome_message: '' });
      navigate(`/app/community/${response.data.community_id}`);
    } catch (error) {
      console.error('Error creating community:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleJoin = async (communityId, e) => {
    e.stopPropagation();
    try {
      const r = await axios.post(`${API}/communities/${communityId}/join`, {}, {
        withCredentials: true
      });
      // Brief 27 oct C.3 — feedback explicite selon la réponse (pending / rejoint / quitté)
      if (r.data?.pending) toast.success("Demande envoyée — l'admin va l'examiner.");
      else if (r.data?.is_member) toast.success('Tu as rejoint la communauté');
      else if (r.data?.is_member === false) toast('Tu as quitté la communauté');
      loadCommunities();
      loadMyCommunities();
    } catch (error) {
      toast.error(error.response?.data?.detail || "L'action n'a pas pu aboutir");
    }
  };

  const filteredCommunities = communities.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto" data-testid="community-list">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-cyan-500 rounded-2xl p-6 mb-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold mb-2">Communautés</h1>
            <p className="text-white/80">
              Rejoignez des communautés pour échanger avec des personnes partageant vos intérêts
            </p>
          </div>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="bg-white text-purple-600 hover:bg-white/90 font-semibold"
          >
            <Plus className="h-4 w-4 mr-2" />
            Créer une communauté
          </Button>
        </div>
      </div>

      {/* My Communities */}
      {myCommunities.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Sparkles className="h-5 w-5 mr-2 text-purple-500" />
            Mes communautés
          </h2>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {myCommunities.map((community) => (
              <div
                key={community.community_id}
                onClick={() => navigate(`/app/community/${community.community_id}`)}
                className="flex-shrink-0 flex items-center gap-3 p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl cursor-pointer hover:shadow-md transition-all"
              >
                <Avatar className="h-12 w-12">
                  <AvatarImage src={community.avatar_url} />
                  <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white font-bold">
                    {community.name?.substring(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium text-gray-900">{community.name}</p>
                  <p className="text-sm text-gray-500">{community.member_count} membre{community.member_count > 1 ? 's' : ''}</p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            placeholder="Rechercher une communauté..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Communities Grid */}
      {filteredCommunities.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune communauté trouvée</h3>
          <p className="text-gray-500 mb-4">
            Soyez le premier à créer une communauté !
          </p>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Créer une communauté
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredCommunities.map((community) => (
            <div
              key={community.community_id}
              onClick={() => navigate(`/app/community/${community.community_id}`)}
              className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all cursor-pointer group"
            >
              {/* Mini Banner */}
              <div 
                className="h-20 relative"
                style={community.banner_gradient 
                  ? { background: `linear-gradient(${community.banner_gradient.direction || 'to right'}, ${community.banner_gradient.color1}, ${community.banner_gradient.color2})` }
                  : { background: 'linear-gradient(to right, #667eea, #764ba2)' }
                }
              >
                <div className="absolute -bottom-8 left-4">
                  <Avatar className="h-16 w-16 border-4 border-white shadow-lg">
                    <AvatarImage src={community.avatar_url} />
                    <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white font-bold">
                      {community.name?.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </div>
              </div>

              <div className="pt-12 px-4 pb-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">
                      {community.name}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm text-gray-500 flex items-center">
                        <Users className="h-3.5 w-3.5 mr-1" />
                        {community.member_count} membre{community.member_count > 1 ? 's' : ''}
                      </span>
                      {/* Sprint 1.0c V1.a-bis — notion "Privée" supprimée ; seul join_mode subsiste */}
                      {community.join_mode === 'invitation' && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium" data-testid={`comm-card-invitation-${community.community_id}`}>
                          Sur invitation
                        </span>
                      )}
                    </div>
                    {community.category && (
                      <span className="inline-block mt-2 px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                        {community.category}
                      </span>
                    )}
                  </div>
                  
                  <Button
                    size="sm"
                    variant={community.is_member || community.has_pending_request ? "outline" : "default"}
                    disabled={community.is_creator || community.has_pending_request}
                    onClick={(e) => (community.is_creator || community.has_pending_request) ? e.stopPropagation() : handleJoin(community.community_id, e)}
                    className={!(community.is_member || community.has_pending_request) ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white" : ""}
                    data-testid={`comm-card-join-${community.community_id}`}
                  >
                    {community.is_creator ? 'Ma communauté' : community.is_member ? "Membre" : community.has_pending_request ? 'Demande envoyée' : community.join_mode === 'invitation' ? "Demander à rejoindre" : "Rejoindre"}
                  </Button>
                </div>
                
                {community.description && (
                  <p className="text-sm text-gray-500 mt-3 line-clamp-2">
                    {community.description}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Community Modal — Brief Jun 16 §3 : max-h 90vh + scroll interne */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col" data-testid="create-community-modal">
            <div className="p-6 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Créer une communauté</h2>
                <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-5 overflow-y-auto flex-1" data-testid="create-community-modal-body">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la communauté *</label>
                <Input
                  value={newCommunity.name}
                  onChange={(e) => setNewCommunity({ ...newCommunity, name: e.target.value })}
                  placeholder="Ex: Product Managers Suisse"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newCommunity.description}
                  onChange={(e) => setNewCommunity({ ...newCommunity, description: e.target.value })}
                  placeholder="Décrivez votre communauté..."
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-300 focus:border-purple-500 outline-none resize-none"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie</label>
                <div className="grid grid-cols-2 gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setNewCommunity({ ...newCommunity, category: cat })}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        newCommunity.category === cat
                          ? 'bg-purple-100 text-purple-700 border-2 border-purple-500'
                          : 'bg-gray-50 text-gray-600 border-2 border-transparent hover:bg-gray-100'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mode d'adhésion</label>
                <div className="flex gap-3">
                  <button
                    data-testid="create-join-mode-open"
                    onClick={() => setNewCommunity({ ...newCommunity, join_mode: 'open' })}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium text-sm transition-all ${
                      newCommunity.join_mode === 'open'
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    Ouverte (1-clic)
                  </button>
                  <button
                    data-testid="create-join-mode-invitation"
                    onClick={() => setNewCommunity({ ...newCommunity, join_mode: 'invitation' })}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium text-sm transition-all ${
                      newCommunity.join_mode === 'invitation'
                        ? 'border-amber-500 bg-amber-50 text-amber-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    Sur invitation
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  En mode "Sur invitation", les demandes attendent votre validation.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message de bienvenue (optionnel)</label>
                <textarea
                  data-testid="create-community-welcome-message"
                  value={newCommunity.welcome_message}
                  onChange={(e) => setNewCommunity({ ...newCommunity, welcome_message: e.target.value })}
                  placeholder="Envoyé en notification aux nouveaux membres."
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-300 outline-none resize-none text-sm"
                  rows={2}
                  maxLength={240}
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 flex gap-3 flex-shrink-0" data-testid="create-community-modal-footer">
              <Button variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1">
                Annuler
              </Button>
              <Button 
                onClick={handleCreateCommunity}
                disabled={!newCommunity.name || creating}
                className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500"
                data-testid="create-community-submit"
              >
                Créer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityList;
